import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import store, { loanActions } from '@apply/adminStore';
import Dialog from '@material-ui/core/Dialog';
import { InformModal, BfiNumber, LoanNumber, BfiCustomStore } from '../../../common/SharedComponents';
import { LoanApi, ListApi, FileApi } from '@apply/api';
import { ConfirmModal } from '@apply/ui';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { v4 as uuidv4 } from 'uuid';
import {
    BClientOrEntitySelector,
    BClientOrEntitySelectorType,
    BrokerSelect,
    LoanProperties,
    BfiNumberSearch,
    ExtensionsGrid,
    LoanExpensesGrid,
    AmortizationTable,
    LoanFilesGrid,
    LoanPortfolioOverageRepaymentGrid,
    LoanTermFields,
} from '../../common/LoanComponents';
import {
    LoanTypeOptions,
    LoanStatusType,
    InterestAcrualTypeOptions,
    InterestAcrualType,
    BuildRentStageType,
    BuildRentStageTypeOptions,
    AmortizationTypeOptions,
    LoanPropertyPurposeType,
} from '../../common/LoanConstants';
import { LoadPanel } from 'devextreme-react/load-panel';
import { NumericFormat } from 'react-number-format';
import { Tooltip } from 'devextreme-react/tooltip';
import { getStatusTypeName, getDrawBudgetURL, findMatchingTermOptionKey } from '../../common/LoanFunctions';
import { addFullYears, addMonths, monthDiff, handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { LoanChangeReasonModal } from '../LoanChangeReasonModal';
import DateBox from 'devextreme-react/date-box';
import { LoanExpenseCategory } from '../../common/constants/LoanExpenseCategory';
import { DialogContentText } from '@material-ui/core';
import { ExitType, FeatureType, LoanFileTypeOptions } from '@apply/constants';
import { useNavigate } from 'react-router-dom';
import { createBudget } from '../../common/functions/draws/createBudget';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import {
    LoanType,
    LoanGradeTypeOptions,
    CallCodeTypeOptions,
    PropertyDevelopmentType,
    PropertyDevelopmentTypeOptions,
} from '@apply/constants';
import { GradeReasonModal } from '../GradeReasonModal';
import { isLoanLoc } from '../../common/functions/isLoanLoc';

export const saveStages = {
    validateStatusChangePrerequisites: 1,
    validateStatusChange: 2,
    validateBfiNumbers: 3,
    validateMaturityDate: 4,
    validateProperties: 5,
    checkHasChanges: 6,
    save: 7,
    openBudget: 8,
    createEmptyBudget: 9,
    createPopulatedBudget: 10,
};

const EditStdLoanModal = () => {
    const {
        isOpen,
        loanId = 0,
        loanType = 0,
        masterNoteId = 0,
        bClientId = 0,
    } = useSelector((state) => state.loan.editStdLoanModal);
    const userState = useSelector((state) => state.user);
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const listApi = new ListApi.ListApi(applicationInsights);

    const navigate = useNavigate();
    const location = useLocation();

    const isFeatureEnabled = (featureId) => {
        if (!userState || !userState.featureList) {
            return false;
        }
        const feature = userState.featureList.find((f) => f.featureId === featureId);
        return feature?.isEnabled ?? false;
    };

    const areDrawsEnabled = useMemo(() => isFeatureEnabled(FeatureType.Draws), [isFeatureEnabled]);

    const styles = {
        sectionHeader: { margin: '10px 0' },
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        formRow: { marginBottom: '0' },
        leftLabel: { paddingTop: '10px', whiteSpace: 'nowrap' },
        bfisearch: {
            width: '250px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        displayFlex: { display: 'flex' },
        inforbearance: { fontSize: '0.75rem', marginTop: 35 },
        warning: {
            padding: '10px',
            color: 'darkred',
            width: '100%',
            textAlign: 'center',
            fontSize: 'smaller',
        },
    };

    const emptyProperty = {
        guid: uuidv4(),
        id: 0,
        legalDescription: '',
        unitCount: null,
        bfiNumberSuffix: 1,
        structureTypeId: '',
        landCost: '',
        verticalCost: '',
        threeMilePopulation: '',
        threeMileFiveYearGrowth: '',
        status: '',
        commitment: '',
        floodReport: {},
        inSpecialFloodHazardArea: false,
        street1: '',
        street2: '',
        city: '',
        state: '',
        zipCode: '',
        county: '',
        isLoanProceedsUsedPropertyImprovements: null,
        moreThan10kExpenditures: null,
        appraisals: [],
        originationFee: '',
        hadPermitAtClosingTypeId: '',
        preferredInterestPaymentTypeId: '',
    };

    const [redirectUrl, set_redirectUrl] = useState(undefined);
    const [employees, set_employees] = useState([]);

    //Loan Fields
    const [isSubNote, set_isSubNote] = useState(false);
    const [isRental, set_isRental] = useState(false);
    const [currentLoanId, set_currentLoanId] = useState(0);
    const [loan, set_loan] = useState(null);

    const [approvalDate, set_approvalDate] = useState(null);
    const [noteDate, set_noteDate] = useState(null);
    const [conversionDeadline, set_conversionDeadline] = useState(null);
    const [maturityDate, set_maturityDate] = useState(null);
    const [selectedTermKey, set_selectedTermKey] = useState('');
    const [initialTermMonths, set_initialTermMonths] = useState(null);
    const [contractualExtension1Months, set_contractualExtension1Months] = useState(null);
    const [contractualExtension2Months, set_contractualExtension2Months] = useState(null);
    const [statusId, set_statusId] = useState(LoanStatusType.Approved);
    const [bfiNumber, set_bfiNumber] = useState('');
    const [loanNumber, set_loanNumber] = useState('');
    const [loanTypeId, set_loanTypeId] = useState(loanType);
    const [pointOfContactId, set_pointOfContactId] = useState('');
    const [underwriterId, set_underwriterId] = useState('');
    const [callCodeType, set_callCodeType] = useState(/** @type {number | null} */ (null));
    const [bClientOrEntity, set_bClientOrEntity] = useState(BClientOrEntitySelectorType.Entity);
    const [borrowerEntityId, set_borrowerEntityId] = useState(null);
    const [brokerId, set_brokerId] = useState(null);
    const [brokerAgreementId, set_brokerAgreementId] = useState(null);
    const [brokerAgreementLocked, set_brokerAgreementLocked] = useState(false);
    const [properties, set_properties] = useState([emptyProperty]);
    const [commitment, set_commitment] = useState(null);
    const [promissoryNote, set_promissoryNote] = useState(null);
    const [bridgeFunded, set_bridgeFunded] = useState(null);
    const [sameAsCommitted, set_sameAsCommitted] = useState(false);
    const isBridgeLoan = loanTypeId === LoanType.Bridge;
    const [isBfiNumberValid, set_isBfiNumberValid] = useState(true);
    const [isLoanNumberValid, set_isLoanNumberValid] = useState(true);
    const [checkNumbers, set_checkNumbers] = useState(null);
    const [interestTypeId, set_interestTypeId] = useState(null);
    const [interestRate, set_interestRate] = useState(null);
    const [interestAcrualTypeId, set_interestAcrualTypeId] = useState(null);
    const [interestRateFloor, set_interestRateFloor] = useState(null);
    const [interestRateCeiling, set_interestRateCeiling] = useState(null);
    const [reserveRate, set_reserveRate] = useState(null);
    const [grade, set_grade] = useState('');
    const [gradeReasonVisible, set_gradeReasonVisible] = useState(false);
    const [gradeReason, set_gradeReason] = useState('');
    const [underwritingFee, set_underwritingFee] = useState(null);
    const [originationFee, set_originationFee] = useState(null);
    const [brokerShare, set_brokerShare] = useState(null);
    const [brokerAdditional, set_brokerAdditional] = useState(null);
    const [expenses, set_expenses] = useState([]);
    const [loanFiles, set_loanFiles] = useState([]);
    const [repaidDate, set_repaidDate] = useState(null);
    const [inForbearance, set_inForbearance] = useState(false);
    const [forbearanceAgreementTerms, set_forbearanceAgreementTerms] = useState('');
    const [liquidity, set_liquidity] = useState(null);
    const [entireStructure, set_entireStructure] = useState(/** @type {boolean | null} */ (null));

    //Rental fields
    const [amortizationTypeId, set_amortizationTypeId] = useState(null);
    const [isAutoPay, set_isAutoPay] = useState(false);
    const [ever30DaysDelinquent, set_ever30DaysDelinquent] = useState(false);

    //Bfi NUmber Check
    const [confirmNumbersModalText, set_confirmNumbersModalText] = useState(false);
    const [confirmNumbersModalOpen, set_confirmNumbersModalOpen] = useState(false);

    //Inform Modal
    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalMessage, set_informModalMessage] = useState('');

    //Move to Outstanding
    const [confirmMoveToOutstandingModalText, set_confirmMoveToOutstandingModalText] = useState([]);
    const [confirmMoveToOutstandingModalOpen, set_confirmMoveToOutstandingModalOpen] = useState(false);

    //Dialogs
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    //ToolTips
    const [instructionsToolTipVisible, set_instructionsToolTipVisible] = useState(false);
    const [brokerBorrowerLocked, set_brokerBorrowerLocked] = useState(false);

    //Maturity Date Warnings
    const maturityAutoCalcRef = useRef(false);
    const preserveLoadedMaturityDate = useRef(false);
    const [maturityDateWarning, set_maturityDateWarning] = useState(false);
    const [maturityDateWarningText, set_maturityDateWarningText] = useState('');
    const [confirmMaturityDateModalText, set_confirmMaturityDateModalText] = useState('');
    const [confirmMaturityDateModalOpen, set_confirmMaturityDateModalOpen] = useState(false);

    //FDILA Terms Warning
    const [confirmFDILAAgreementTextRemovalWarningOpen, set_confirmFDILAAgreementTextRemovalWarningOpen] = useState(false);
    const [confirmFDILAAgreementTextRemovalWarningText, set_confirmFDILAAgreementTextRemovalWarningText] = useState(
        'Removing this Agreement will also remove the Agreement Terms.'
    );

    //Changes Modal
    const [changesModalOpen, set_changesModalOpen] = useState(false);
    const [pendingCreateBudgetMode, set_pendingCreateBudgetMode] = useState(undefined);
    const [pendingBudgetProperty, set_pendingBudgetProperty] = useState(undefined);

    const handleClose = () =>
        dispatch(
            loanActions.setEditStdLoanModal({
                editStdLoanModal: { isOpen: false },
            })
        );

    useEffect(() => {
        loadBfiEmployees();
    }, []);

    useEffect(() => {
        async function loanTypeChanged() {
            if (!loanType) {
                return;
            }
            await createNewLoan(loanType);
        }

        loanTypeChanged();
    }, [loanType]);

    useEffect(() => {
        async function isOpenChanged() {
            if (!isOpen) return;

            resetState();
            set_isBfiNumberValid(true);
            set_isLoanNumberValid(true);
            set_checkNumbers(null);
            if (loanId > 0) {
                await loadFromLoanId(loanId);
            } else if (masterNoteId && masterNoteId > 0) {
                set_isSubNote(true);
                await createFromMasterNote(masterNoteId);
            } else if (bClientId > 0) {
                await createForBClient(bClientId);
            }
            set_loadingOpen(false);
        }

        isOpenChanged();
    }, [loanId, masterNoteId, bClientId, isOpen]);

    useEffect(() => {
        const keepLoadedMaturityDate = preserveLoadedMaturityDate.current;
        preserveLoadedMaturityDate.current = false;

        if (!noteDate) {
            return;
        }
        if (!conversionDeadline) {
            const dt = new Date(noteDate.getTime());
            set_conversionDeadline(addFullYears(dt, 5));
        }
        if (!isSubNote && initialTermMonths && !keepLoadedMaturityDate) {
            const dt = new Date(noteDate.getTime());
            maturityAutoCalcRef.current = true;
            set_maturityDate(addMonths(dt, initialTermMonths));
        }
    }, [noteDate, initialTermMonths]);

    useEffect(() => {
        if (maturityAutoCalcRef.current) {
            maturityAutoCalcRef.current = false;
            set_maturityDateWarning(false);
            set_maturityDateWarningText('');
            return;
        }
        if (isSubNote || !noteDate || !initialTermMonths || !maturityDate) {
            set_maturityDateWarning(false);
            set_maturityDateWarningText('');
            return;
        }
        var months = monthDiff(noteDate, maturityDate);
        if (months !== initialTermMonths) {
            set_maturityDateWarning(true);
            if (months > initialTermMonths) {
                set_maturityDateWarningText('Maturity Date Is Past the Loan Terms.');
            } else {
                set_maturityDateWarningText('Maturity Date Is Less than the Loan Terms.');
            }
        } else {
            set_maturityDateWarning(false);
            set_maturityDateWarningText('');
        }
    }, [noteDate, initialTermMonths, maturityDate]);

    const loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const validateBfiNumbers = async (force) => {
        if (!bfiNumber && !loanNumber) {
            set_checkNumbers(null);
            set_isBfiNumberValid(true);
            set_isLoanNumberValid(true);
            return true;
        }

        var newChecks = (bfiNumber ?? '') + (loanNumber ?? '');
        if (!force && (!isBfiNumberValid || !isLoanNumberValid) && checkNumbers === newChecks) {
            return;
        }
        set_checkNumbers(newChecks);

        if (loanNumber && !bfiNumber) {
            //Can't have a loan number without a BFI Number
            set_loadingOpen(false);
            set_isBfiNumberValid(false);
            set_informModalMessage('A Loan # requires that a BFI Number is also entered.');
            set_informModalOpen(true);
            return false;
        }

        const model = {
            id: loan?.id ?? 0,
            bfiNumber,
            loanNumber,
            isMasterNote: false,
        };

        const response = await loanApi.checkLoanAndBfiNumbers(model);
        if (response.statusCode !== 200) {
            return true;
        }
        const result = response.result;
        let msg = result.message;
        set_isBfiNumberValid(result.bfiNumberValid);
        set_isLoanNumberValid(result.loanNumberValid);
        if (result.isValid) {
            if (msg) {
                set_loadingOpen(false);
                msg += ' Do you wish to continue?';
                set_confirmNumbersModalText(msg);
                set_confirmNumbersModalOpen(true);
                return false;
            }
        } else {
            set_loadingOpen(false);
            set_informModalMessage(msg);
            set_informModalOpen(true);
            return false;
        }
        return true;
    };

    const validatePropertiesForBridge = (properties) => {
        const nonAcqAcqRefi = [
            LoanPropertyPurposeType.NonAcquisition,
            LoanPropertyPurposeType.Acquisition,
            LoanPropertyPurposeType.Refinance,
        ];
        const purposeAllowedNames = 'Non-Acquisition, Acquisition, or Refinance';
        const scenarioName = (id) => PropertyDevelopmentTypeOptions.find((o) => o.id === id)?.name ?? '';
        const isEmpty = (v) => v == null || Number.isNaN(v);
        const propertyLabel = (index) => (properties.length > 1 ? `Property .${(index + 1).toString().padStart(2, '0')}: ` : '');

        const fail = (msg, index) => {
            properties[index].inValid = true;
            set_properties([...properties]);
            set_informModalMessage(`${propertyLabel(index)}${msg}`);
            set_informModalOpen(true);
        };

        for (let i = 0; i < properties.length; i++) {
            let property = properties[i];

            const scenario = isEmpty(property.propertyDevelopmentTypeId) ? null : property.propertyDevelopmentTypeId;
            const purpose = isEmpty(property.loanPropertyPurposeTypeId) ? null : property.loanPropertyPurposeTypeId;

            // Scenario "Other" requires a description
            if (scenario === PropertyDevelopmentType.Other && isEmpty(property.propertyDevelopmentTypeOther)) {
                fail('When Scenario is "Other", a Scenario (Other) description is required.', i);
                return false;
            }

            // Construction/Renovation scenarios require Year Built and Owned-Through-Development
            const isConstructionOrRenovation =
                scenario === PropertyDevelopmentType.NewConstruction ||
                scenario === PropertyDevelopmentType.Renovation ||
                scenario === PropertyDevelopmentType.RenovationAndExpansion;
            if (isConstructionOrRenovation) {
                if (isEmpty(property.yearBuilt)) {
                    fail(`When Scenario is "${scenarioName(scenario)}", Year Built is required.`, i);
                    return false;
                }
                if (property.ownedThroughDevelopment == null) {
                    fail(`When Scenario is "${scenarioName(scenario)}", Owned Through Development is required.`, i);
                    return false;
                }
            }

            // Scenario + Purpose rules
            if (scenario === PropertyDevelopmentType.NewAcquisition) {
                if (purpose == null || !nonAcqAcqRefi.includes(purpose)) {
                    fail(`When Scenario is "${scenarioName(scenario)}", Purpose must be ${purposeAllowedNames}.`, i);
                    return false;
                }
            } else if (isConstructionOrRenovation) {
                const currentDebt = Number(property.currentDebt) || 0;
                if (currentDebt > 0) {
                    if (purpose !== LoanPropertyPurposeType.Refinance) {
                        fail(
                            `When Scenario is "${scenarioName(scenario)}" and Current Debt is greater than $0, Purpose must be Refinance.`,
                            i
                        );
                        return false;
                    }
                } else if (purpose == null || !nonAcqAcqRefi.includes(purpose)) {
                    fail(
                        `When Scenario is "${scenarioName(scenario)}" and Current Debt is $0, Purpose must be ${purposeAllowedNames}.`,
                        i
                    );
                    return false;
                }
            }

            // Work Needed requires a description
            if (property.workNeeded === true && isEmpty(property.workNeededDescription)) {
                fail('When Work Needed is "Yes", a Work Description is required.', i);
                return false;
            }

            // Exit Type requires a matching amount
            if (property.exitType != null) {
                const exit = Number(property.exitType);
                if (exit === ExitType.Sale && isEmpty(property.expectedListingPrice)) {
                    fail('When Exit Type is "Sale", an Expected Listing Price is required.', i);
                    return false;
                }
                if (exit === ExitType.Refinance && isEmpty(property.expectedTakeOutLoanAmount)) {
                    fail('When Exit Type is "Refinance", an Expected Take-Out Loan Amount is required.', i);
                    return false;
                }
            }
        }

        return true;
    };

    const validateProperties = (event, properties) => {
        //Property Appraisal Check
        if (statusId === LoanStatusType.Approved || statusId === LoanStatusType.Outstanding) {
            const missingAppraisalProps = properties.filter((p) => p.id > 0 && p.appraisals.length === 0);
            if (missingAppraisalProps && missingAppraisalProps.length > 0) {
                set_informModalMessage('Please add an Appraisal value under the Property Appraisal section.');
                set_informModalOpen(true);
                return false;
            }
        }

        if (loanTypeId === LoanType.Bridge && !validatePropertiesForBridge(properties)) {
            return false;
        }

        if (properties.length === 1) {
            return true; //Don't need to validate in this case as it will be handles by the regular form submit
        }

        properties.forEach((p) => (p.inValid = false));

        for (let x = 0; x < properties.length; x++) {
            const p = properties[x];
            let isValid = false;
            let msg = '';
            if (noteDate) {
                isValid =
                    !!p.legalDescription &&
                    !!p.unitCount &&
                    !!p.street1 &&
                    !!p.city &&
                    !!p.state &&
                    !!p.zipCode &&
                    !!p.commitment;
                if (loanTypeId !== LoanType.Land) {
                    isValid = isValid && !!p.structureTypeId;
                }
                msg = `Property .${(x + 1).toString().padStart(2, '0')} is incomplete!`;
            } else {
                isValid = !!p.legalDescription;
                if (loanTypeId !== LoanType.Land) {
                    isValid = isValid && !!p.structureTypeId && !!p.unitCount;
                    msg = 'Each property requires at least a unique Legal description, Structure Type and Unit Count.';
                } else {
                    msg = 'Each property requires at least a unique Legal description';
                }
            }

            if (!isValid) {
                p.inValid = true;
                set_properties([...properties]);
                if (msg) {
                    set_informModalMessage(msg);
                    set_informModalOpen(true);
                }
                return false;
            }
        }

        //Ensure legal names are unique
        const descs = new Set(properties.map((item) => item.legalDescription));
        if (descs.size !== properties.length) {
            set_informModalMessage('Each property must have a unique Legal Description!');
            set_informModalOpen(true);
            return false;
        }

        return true;
    };

    const isAnyPropertyAcquisition = (properties) => {
        const propsWithAcquisition = properties.filter(
            (i) => i.loanPropertyPurposeTypeId === LoanPropertyPurposeType.Acquisition
        );
        if (propsWithAcquisition.length > 0) {
            return true;
        }
        return false;
    };

    const validateSubmission = async (e, step, property = undefined) => {
        if (e) {
            e.preventDefault();
        }
        step = step ?? saveStages.start;

        set_loadingMessage('Validating...');
        set_loadingOpen(true);

        //Check if in Approved State and user entered a NoteDate - and a budget has not been created - LOC specific check.
        if (step === saveStages.validateStatusChangePrerequisites) {
            if (
                areDrawsEnabled &&
                statusId === LoanStatusType.Approved &&
                noteDate &&
                loanTypeId !== LoanType.Rental &&
                loanTypeId !== LoanType.Bridge
            ) {
                set_informModalMessage([]);
                set_loadingOpen(false);
                let requiresInfoAlert = false;

                if (loan.properties.some((property) => property.drawBudgetId === null)) {
                    set_informModalMessage((confirmPreRequisiteText) => [
                        ...confirmPreRequisiteText,
                        'A budget is required for a loan to move from Approved to Outstanding.',
                    ]);
                    requiresInfoAlert = true;
                }
                if (requiresInfoAlert) {
                    set_informModalOpen(true);
                    return false;
                } else {
                    step = saveStages.validateStatusChange;
                }
            } else {
                step = saveStages.validateStatusChange;
            }
        }

        //Check if in Approved State and user entered a NoteDate
        if (step === saveStages.validateStatusChange) {
            if (statusId === LoanStatusType.Approved && noteDate) {
                //Reset Modal Text
                set_confirmMoveToOutstandingModalText([]);
                set_loadingOpen(false);
                //Compile modal text
                set_confirmMoveToOutstandingModalText((confirmMoveToOutstandingModalText) => [
                    ...confirmMoveToOutstandingModalText,
                    'Setting the Note Date will cause the loan to move to Outstanding (Closed) status. Are you sure?',
                ]);
                if (isAnyPropertyAcquisition(properties)) {
                    set_confirmMoveToOutstandingModalText((confirmMoveToOutstandingModalText) => [
                        ...confirmMoveToOutstandingModalText,
                        'Loan requires funding at closing - please confirm it is an acquisition.',
                    ]);
                }
                set_confirmMoveToOutstandingModalOpen(true);
                return false;
            }
            step = saveStages.validateBfiNumbers;
        }

        //Check Loan and BfiNumbers to ensure they are unique
        if (step === saveStages.validateBfiNumbers) {
            const areNumbersOk = await validateBfiNumbers(true);
            if (!areNumbersOk) {
                //set_loadingOpen(false);
                return false;
            }
            step = saveStages.validateMaturityDate;
        }

        //Alert if the Maturity Date is potentially wrong
        if (step === saveStages.validateMaturityDate) {
            if (maturityDateWarning) {
                //alert and continue if confirmed
                set_loadingOpen(false);
                set_confirmMaturityDateModalText(maturityDateWarningText);
                set_confirmMaturityDateModalOpen(true);
                return false;
            }
            step = saveStages.validateProperties;
        }

        //If Multiple addresses - Validate that addresses are complete
        if (step === saveStages.validateProperties) {
            const arePropsOk = validateProperties(e, properties);
            if (!arePropsOk) {
                set_loadingOpen(false);
                return false;
            }
            step = saveStages.checkHasChanges;
        }

        if (step === saveStages.createEmptyBudget || step === saveStages.createPopulatedBudget) {
            set_redirectUrl(undefined);
        }

        if (step === saveStages.openBudget) {
            set_redirectUrl(getDrawBudgetURL(property.drawBudgetId, location));
        }

        let saveNoteDate = true;
        let localRedirectUrl = undefined;
        let createBudgetMode = undefined;
        if (step === saveStages.openBudget && statusId === LoanStatusType.Approved) {
            // Do not allow saving note date when opening the budget on an approved loan
            saveNoteDate = false;
            localRedirectUrl = getDrawBudgetURL(property.drawBudgetId, location);
        }

        if (step === saveStages.createEmptyBudget || step === saveStages.createPopulatedBudget) {
            if (statusId === LoanStatusType.Approved) {
                saveNoteDate = false;
            }
            createBudgetMode = step;
            localRedirectUrl = undefined;
        }

        if (
            step === saveStages.checkHasChanges ||
            step === saveStages.openBudget ||
            step === saveStages.createEmptyBudget ||
            step === saveStages.createPopulatedBudget
        ) {
            const hasChanges = hasOutandingLoanChanges();

            if (hasChanges) {
                //Display modal and allow user to enters reason for change
                set_pendingCreateBudgetMode(createBudgetMode);
                set_pendingBudgetProperty(property);
                set_changesModalOpen(true);
                set_loadingOpen(false);
                return false;
            } else {
                step = saveStages.save;
            }
        }

        if (step === saveStages.save) {
            const budgetMode = pendingCreateBudgetMode || createBudgetMode;
            const budgetProperty = pendingBudgetProperty || property;
            set_pendingCreateBudgetMode(undefined);
            set_pendingBudgetProperty(undefined);
            await saveLoan(null, saveNoteDate, localRedirectUrl, budgetMode, budgetProperty);
        }
    };

    const getModel = () => {
        const model = {
            id: loan?.id,
            type: loan?.type,
            masterNoteId: loan?.masterNoteId,
            approvalDate: approvalDate ? new Date(approvalDate).toLocaleDateString('en-us') : null,
            noteDate: noteDate ? new Date(noteDate).toLocaleDateString('en-us') : null,
            conversionDeadline: conversionDeadline ? new Date(conversionDeadline).toLocaleDateString('en-us') : null,
            initialTermMonths,
            contractualExtension1Months,
            contractualExtension2Months,
            maturityDate: maturityDate ? new Date(maturityDate).toLocaleDateString('en-us') : null,
            bfiNumber,
            loanNumber,
            loanTypeId,
            statusId,
            brokerAgreementId,
            borrowerEntityId: borrowerEntityId,
            brokerId: brokerId,
            pointOfContactId: !pointOfContactId || pointOfContactId === '' ? null : pointOfContactId,
            underwriterId: !underwriterId || underwriterId === '' ? null : underwriterId,
            callCodeType,
            properties,
            applicationId: loan?.applicationId,
            commitment,
            promissoryNote,
            bridgeFunded,
            interestRate,
            interestAcrualTypeId: interestAcrualTypeId,
            interestRateFloor,
            interestRateCeiling,
            reserveRate,
            grade: grade > 0 ? grade : null,
            gradeReason,
            underwritingFee,
            originationFee,
            brokerShare,
            brokerAdditional,
            expenses,
            //Rental Fields
            amortizationTypeId,
            isAutoPay,
            ever30DaysDelinquent,
            repaidDate: repaidDate ? new Date(repaidDate).toLocaleDateString('en-us') : null,
            inForbearance,
            forbearanceAgreementTerms,
            liquidity,
            entireStructure,
        };

        model.files = (loanFiles ?? [])
            .filter((f) => f.isDeleted || f.tempFileName)
            .map((f) => {
                return {
                    id: f.id,
                    fileTypeId: f.fileTypeId,
                    fileName: f.fileName,
                    tempFileName: f.tempFileName,
                    isDeleted: f.isDeleted,
                };
            });

        model.properties.forEach((p) => {
            if (!p.files) {
                p.files = [];
            }
            p.files = p.files
                .filter((f) => f.isDeleted || f.tempFileName)
                .map((f) => {
                    return {
                        id: f.id,
                        fileTypeId: f.fileTypeId,
                        fileName: f.fileName,
                        tempFileName: f.tempFileName,
                        isDeleted: f.isDeleted,
                    };
                });
        });

        return model;
    };

    const saveLoan = async (
        changesModel,
        saveNoteDate = true,
        localRedirectUrl = undefined,
        createBudgetMode = undefined,
        property = undefined
    ) => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            //Compile list of files to upload
            const filesToUpload = [];

            if (loanFiles && loanFiles.length > 0) {
                loanFiles.forEach((f) => {
                    if (f.fileToUpload) {
                        filesToUpload.push(f);
                    }
                });
            }

            properties.forEach((p) => {
                if (p.floodReport && p.floodReport.fileToUpload) {
                    filesToUpload.push(p.floodReport);
                }
                if (p.files && p.files.length > 0) {
                    p.files.forEach((f) => {
                        if (f.fileToUpload) {
                            filesToUpload.push(f);
                        }
                    });
                }
                p.appraisals.forEach((a) => {
                    if (a.fileToUpload) {
                        filesToUpload.push(a);
                    }
                });
            });

            if (filesToUpload.length > 0) {
                set_loadingMessage(`Uploading ${filesToUpload.length} File(s)...`);
                await Promise.all(
                    filesToUpload.map(async (f) => {
                        const upLoadResult = await fileApi.uploadTempFiles([f.fileToUpload]);
                        const result = upLoadResult.result;
                        if (result && result.length > 0) {
                            f.tempFileName = result[0].tempName;
                        }
                    })
                );
            }

            const model = getModel();
            model.changeSummary = changesModel;

            // Reset Note Date if we are not saving it
            if (!saveNoteDate) model.noteDate = loan.noteDate;

            //Save main
            set_loadingMessage('Saving Loan changes...');

            const response = await loanApi.updateStandardLoan(model);

            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            if (createBudgetMode === saveStages.createEmptyBudget) {
                set_loadingMessage('Creating Empty Budget...');
                set_loadingOpen(true);
                let budgetId = await createBudget(property.id, false);
                localRedirectUrl = getDrawBudgetURL(budgetId, location);
                set_loadingOpen(false);
            } else if (createBudgetMode === saveStages.createPopulatedBudget) {
                set_loadingMessage('Creating Populated Budget...');
                set_loadingOpen(true);
                let budgetId = await createBudget(property.id, true);
                localRedirectUrl = getDrawBudgetURL(budgetId, location);
                set_loadingOpen(false);
            }

            handleAdminSuccess('Loan Saved successfully.');

            localRedirectUrl = localRedirectUrl ?? redirectUrl;
            setTimeout(() => {
                handleClose();
                if (localRedirectUrl) navigate(localRedirectUrl);
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const confirmNumber_confirm = async () => {
        set_confirmNumbersModalOpen(false);
        await validateSubmission(null, saveStages.checkHasChanges);
    };

    const confirmNumber_cancel = () => {
        set_confirmNumbersModalOpen(false);
        set_loadingOpen(false);
    };

    const confirmMaturityDate_confirm = async () => {
        set_confirmMaturityDateModalOpen(false);
        await validateSubmission(null, saveStages.validateProperties);
    };

    const confirmMaturityDate_cancel = () => {
        set_confirmMaturityDateModalOpen(false);
        set_confirmMaturityDateModalText('');
        set_loadingOpen(false);
    };

    const confirmMoveToOutstanding_confirm = async () => {
        set_confirmMoveToOutstandingModalOpen(false);
        await validateSubmission(null, saveStages.validateBfiNumbers);
    };

    const confirmMoveToOutstanding_cancel = async () => {
        set_confirmMoveToOutstandingModalOpen(false);
        set_loadingOpen(false);
    };

    const confirmFDILAAgreementTextRemoval_confirm = async () => {
        set_confirmFDILAAgreementTextRemovalWarningOpen(false);
        set_inForbearance(false);
        set_forbearanceAgreementTerms('');
    };

    const confirmFDILAAgreementTextRemoval_cancel = () => {
        set_confirmFDILAAgreementTextRemovalWarningOpen(false);
    };

    const hasOutandingLoanChanges = () => {
        if (loan.statusId !== LoanStatusType.Outstanding) {
            return false;
        }

        //Fields of Interest
        //Setup: Approval date, Note date, Term, Maturity date, BFI #, Loan #,
        //Loan: Commitment, Interest rate, Interest Accrual, interestRateFloor, interestRateCeiling, Reserve Rate, Underwriting Fee,
        //  Origination Fee, Broker Share, Broker Share percent, Broker Addl, Broker Addl Percent
        //Rental Data: Amortization, AutoPay, Ever 30+ Delinquent,
        //Property (each): Address, Commitment, Holdback, Purpose, Origination Fee
        //Expenses (each): Name, InvoiceDate, Amount, BorrowerPayment, Vendor, Notes
        let hasChanges = false;

        if (loan.approvalDate) {
            hasChanges = new Date(loan.approvalDate).toLocaleDateString('en-us') !== approvalDate?.toLocaleDateString('en-us');
        }
        if (hasChanges) {
            return true;
        }

        if (loan.noteDate) {
            hasChanges = new Date(loan.noteDate).toLocaleDateString('en-us') !== noteDate?.toLocaleDateString('en-us');
        }
        if (hasChanges) {
            return true;
        }

        if (loan.maturityDate) {
            hasChanges = new Date(loan.maturityDate).toLocaleDateString('en-us') !== maturityDate?.toLocaleDateString('en-us');
        }
        if (hasChanges) {
            return true;
        }

        hasChanges =
            loan.inForbearance !== inForbearance ||
            loan.initialTermMonths !== initialTermMonths ||
            loan.contractualExtension1Months !== contractualExtension1Months ||
            loan.contractualExtension2Months !== contractualExtension2Months ||
            loan.bfiNumber !== bfiNumber ||
            loan.loanNumber !== loanNumber ||
            loan.commitment !== commitment ||
            loan.promissoryNote !== promissoryNote ||
            loan.bridgeFunded !== bridgeFunded ||
            loan.interestRate !== interestRate ||
            loan.interestAcrualTypeId !== interestAcrualTypeId ||
            loan.interestRateFloor !== interestRateFloor ||
            loan.interestRateCeiling !== interestRateCeiling ||
            loan.reserveRate !== reserveRate ||
            loan.grade !== grade ||
            loan.underwritingFee !== underwritingFee ||
            loan.originationFee !== originationFee ||
            loan.brokerShare !== brokerShare ||
            loan.brokerAdditional !== brokerAdditional ||
            loan.liquidity !== liquidity;

        if (hasChanges) {
            return true;
        }

        if (loan.isRental) {
            hasChanges =
                loan.amortizationTypeId !== amortizationTypeId ||
                loan.isAutoPay !== isAutoPay ||
                loan.ever30DaysDelinquent !== ever30DaysDelinquent;

            if (hasChanges) {
                return true;
            }
        }

        hasChanges = loan.properties.length !== properties.length; //Property added or deleted
        if (hasChanges) {
            return true;
        }

        if (expenses.length !== loan.expenses.length) {
            hasChanges = true;
        } else {
            //loop through each expense and compare items
            for (let x = 0; x < loan.expenses.length; x++) {
                const origExp = loan.expenses[x];
                const updatedExp = expenses.find((i) => i.id === origExp.id);
                if (updatedExp) {
                    hasChanges =
                        origExp.name !== updatedExp.name ||
                        origExp.invoiceDate !== updatedExp.invoiceDate ||
                        origExp.amount !== updatedExp.amount ||
                        origExp.borrowerPayment !== updatedExp.borrowerPayment ||
                        origExp.vendor !== updatedExp.vendor ||
                        origExp.notes !== updatedExp.notes;

                    if (hasChanges) {
                        break;
                    }
                }
            }
        }

        if (hasChanges) {
            return true;
        }

        for (let x = 0; x < loan.properties.length; x++) {
            const origProp = loan.properties[x];
            const updatedProp = properties.find((p) => p.id === origProp.id);
            if (updatedProp) {
                var origAppraisals = JSON.stringify(origProp.appraisals ?? []);
                const newAppraisals = JSON.stringify(updatedProp.appraisals ?? []);
                hasChanges = origAppraisals !== newAppraisals;
                origProp.legalDescription !== updatedProp.legalDescription ||
                    origProp.structureTypeId !== updatedProp.structureTypeId ||
                    origProp.unitCount !== updatedProp.unitCount ||
                    origProp.street1 !== updatedProp.street1 ||
                    origProp.street2 !== updatedProp.street2 ||
                    origProp.city !== updatedProp.city ||
                    origProp.state !== updatedProp.state ||
                    origProp.zipCode !== updatedProp.zipCode ||
                    origProp.county !== updatedProp.county ||
                    origProp.commitment !== updatedProp.commitment ||
                    origProp.landCost !== updatedProp.landCost ||
                    origProp.verticalCost !== updatedProp.verticalCost ||
                    origProp.status !== updatedProp.status ||
                    origProp.threeMilePopulation !== updatedProp.threeMilePopulation ||
                    origProp.threeMileFiveYearGrowth !== updatedProp.threeMileFiveYearGrowth ||
                    origProp.loanPropertyPurposeTypeId !== updatedProp.loanPropertyPurposeTypeId ||
                    origProp.currentDebt !== updatedProp.currentDebt ||
                    origProp.originationFee !== updatedProp.originationFee;

                if (hasChanges) {
                    break;
                }

                if (loan.isRental) {
                    hasChanges = origProp.rentHoldback !== updatedProp.rentHoldback;

                    if (hasChanges) {
                        break;
                    }
                }
            }
        }

        return hasChanges;
    };

    const onLoanTypeChange = (input) => {
        if (loanTypeId === input) {
            return;
        }
        if (!input) {
            set_loanTypeId(loanType);
        } else {
            set_loanTypeId(parseInt(input));
        }
    };

    const resetState = () => {
        const lt = loan?.loanTypeId ?? loanType ?? 0;
        const isSN = lt === LoanType.SubNoteConstruction || lt === LoanType.SubNoteFixFlip;
        set_isSubNote(isSN);
        set_bClientOrEntity(BClientOrEntitySelectorType.Entity);
        set_currentLoanId(0);
        set_loan(null);
        set_approvalDate(null);
        set_conversionDeadline(null);
        set_noteDate(null);
        set_selectedTermKey('');
        set_initialTermMonths(null);
        set_contractualExtension1Months(null);
        set_contractualExtension2Months(null);
        set_maturityDate(null);
        preserveLoadedMaturityDate.current = false;
        set_statusId(LoanStatusType.Approved);
        set_bfiNumber('');
        set_loanNumber('');
        set_loanTypeId(lt);
        set_pointOfContactId('');
        set_underwriterId('');
        set_callCodeType(null);
        set_borrowerEntityId(null);
        set_brokerId(null);
        set_brokerAgreementId(null);
        set_brokerAgreementLocked(false);
        set_properties([emptyProperty]);

        set_commitment(null);
        set_promissoryNote(null);
        set_bridgeFunded(null);
        set_sameAsCommitted(false);
        set_interestTypeId(null);
        set_interestRate(null);
        set_interestAcrualTypeId(null);
        set_interestRateFloor(null);
        set_interestRateCeiling(null);
        set_reserveRate(null);
        set_grade('');

        set_underwritingFee(null);
        set_originationFee(null);
        set_brokerShare(null);
        set_brokerAdditional(null);
        set_expenses([]);
        set_loanFiles([]);

        //Rental fields
        set_isRental(false);
        set_amortizationTypeId(null);
        set_isAutoPay(false);
        set_ever30DaysDelinquent(false);

        set_repaidDate(null);
        set_inForbearance(false);
        set_forbearanceAgreementTerms('');
        set_liquidity(null);
        set_entireStructure(null);
    };

    const onPropertiesChange = (props) => {
        set_properties([...props]);
        if (props.length > 1) {
            let commitmentSum = 0;
            let originationFeeSum = 0;
            props.forEach((p) => {
                commitmentSum += p.commitment ?? 0;
                originationFeeSum += p.originationFee ?? 0;
            });
            //Bridge loans enter the approved amount manually.
            if (!isBridgeLoan) {
                set_commitment(commitmentSum);
            }
            set_originationFee(originationFeeSum);
        }
    };

    const onPropertiesRemove = (props) => {
        set_properties([...props]);

        let commitmentSum = 0;
        let originationFeeSum = 0;
        props.forEach((p) => {
            commitmentSum += p.commitment ?? 0;
            originationFeeSum += p.originationFee ?? 0;
        });
        //Bridge loans enter the approved amount manually.
        if (!isBridgeLoan) {
            set_commitment(commitmentSum);
        }
        set_originationFee(originationFeeSum);
    };

    const onInformModalConfirm = () => {
        set_informModalOpen(false);
    };

    const selectBfiNumber = async (bfiNumber) => {
        //Load Loan details from BFI Number
        if (!bfiNumber) {
            return;
        }

        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            resetState();
            const response = await loanApi.getStandardLoanByBfiNumber(bfiNumber);
            if (response.statusCode !== 200) {
                return;
            }
            const loan = response.result;
            populateState(loan);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const onBClientOrEntitySelection = (selectionType, id) => {
        if (isSubNote) {
            return;
        }

        set_bClientOrEntity(selectionType);
        if (selectionType === BClientOrEntitySelectorType.BClient) {
            if (isSubNote) {
                return;
            }
            set_brokerAgreementId(id);
            return;
        }

        set_borrowerEntityId(id);
        if (selectionType === BClientOrEntitySelectorType.Entity) {
            set_brokerAgreementId(null);
        }
    };

    const onBrokerSelect = (broker) => {
        if (isSubNote) {
            return;
        }

        if (brokerAgreementId || !broker) {
            set_brokerId(null);
            return;
        }

        set_brokerId(broker.id);
    };

    const createFromMasterNote = async (masterNoteId) => {
        try {
            set_loadingMessage('Creating Sub Note...');
            set_loadingOpen(true);

            resetState();
            const response = await loanApi.createSubnote(masterNoteId);
            if (response.statusCode !== 200) {
                return;
            }

            const mnLoan = response.result;
            set_loan(mnLoan);
            populateState(mnLoan);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const createForBClient = async (brokerAgreementId) => {
        try {
            set_loadingMessage('Creating Loan for B-Client...');
            set_loadingOpen(true);

            resetState();
            const response = await loanApi.createStandardLoan(null, brokerAgreementId);
            if (response.statusCode !== 200) {
                return;
            }

            const loan = response.result;
            set_loan(loan);
            populateState(loan);
            set_brokerAgreementLocked(true);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const loadFromLoanId = async (loanId) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            resetState();
            const response = await loanApi.getStandardLoanById(loanId);
            if (response.statusCode !== 200) {
                return;
            }
            const loan = response.result;
            populateState(loan);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const createNewLoan = async (loanTypeId) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            resetState();
            const response = await loanApi.createStandardLoan(loanTypeId);
            if (response.statusCode !== 200) {
                return;
            }
            const loan = response.result;
            populateState(loan);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const populateState = (loan) => {
        if (!loan) {
            resetState();
            return;
        }

        const isSN = isLoanLoc(loan.loanTypeId);
        set_isSubNote(isSN);
        set_currentLoanId(loan.id);

        const clone = JSON.parse(JSON.stringify(loan));
        set_loan(clone);

        if (loan.approvalDate) {
            set_approvalDate(new Date(loan.approvalDate));
        }
        if (loan.noteDate) {
            set_noteDate(new Date(loan.noteDate));
        }
        // Set term values - for sub-notes use fixed 60 months
        if (isSN) {
            set_initialTermMonths(60);
            set_contractualExtension1Months(null);
            set_contractualExtension2Months(null);
            set_selectedTermKey('60-0');
        } else {
            set_initialTermMonths(loan.initialTermMonths);
            set_contractualExtension1Months(loan.contractualExtension1Months);
            set_contractualExtension2Months(loan.contractualExtension2Months);
            // Determine which dropdown option matches the loan's term values
            const termKey = findMatchingTermOptionKey(
                loan.loanTypeId ?? loanType ?? 0,
                loan.initialTermMonths,
                loan.contractualExtension1Months,
                loan.contractualExtension2Months
            );
            set_selectedTermKey(termKey);
        }
        if (loan.maturityDate) {
            // When loading an existing loan with a maturity date already set, prevent auto recalculation
            // (unless and until the user manually changes the note date or term)
            preserveLoadedMaturityDate.current = true;
            set_maturityDate(new Date(loan.maturityDate));
        }

        const loanTypeVal = loan.loanTypeId ?? loanType ?? 0;
        set_loanTypeId(loanTypeVal);
        set_statusId(loan.statusId);
        set_bfiNumber(loan.bfiNumber);
        set_loanNumber(loan.loanNumber ?? '');
        set_pointOfContactId(loan.pointOfContactId ?? '');
        set_underwriterId(loan.underwriterId ?? '');
        set_callCodeType(loan.callCodeType ?? null);

        set_borrowerEntityId(loan.borrowerEntityId);
        set_brokerId(loan.brokerId);
        if (loan.brokerAgreementId) {
            set_bClientOrEntity(BClientOrEntitySelectorType.BClient);
            set_brokerAgreementId(loan.brokerAgreementId);
        } else {
            set_bClientOrEntity(BClientOrEntitySelectorType.Entity);
        }

        if (loanTypeVal === LoanType.BuildRent) {
            if (loan.conversionDeadline) {
                set_conversionDeadline(new Date(loan.conversionDeadline));
            }
        }
        set_interestTypeId(loan.interestTypeId);
        set_interestRate(loan.interestRate);
        if (loan.interestAcrualTypeId != null && loan.interestAcrualTypeId != undefined && loan.interestAcrualTypeId >= 0) {
            set_interestAcrualTypeId(loan.interestAcrualTypeId);
        } else {
            if (loanTypeVal === LoanType.Rental || loanTypeVal === LoanType.Bridge) {
                set_interestAcrualTypeId(InterestAcrualType.Thirty360);
            } else {
                set_interestAcrualTypeId(InterestAcrualType.Actual360);
            }
        }
        set_interestRateFloor(loan.interestRateFloor);
        set_interestRateCeiling(loan.interestRateCeiling);
        set_reserveRate(loan.reserveRate);
        set_grade(loan.grade ?? '');

        set_underwritingFee(loan.underwritingFee);
        set_originationFee(loan.originationFee);
        set_brokerShare(loan.brokerShare);
        set_brokerAdditional(loan.brokerAdditional);
        set_expenses(loan.expenses);
        set_loanFiles(loan.files);

        const props = loan.properties && loan.properties.length > 0 ? loan.properties : [emptyProperty];
        set_properties(props);

        //NOTE: use loan.loanTypeId, not the loanTypeId state - state is not set yet at this point.
        if (loan.loanTypeId !== LoanType.Bridge && props.length > 1) {
            let sum = 0;
            props.forEach((p) => {
                sum += p.commitment ?? 0;
            });
            set_commitment(sum);
        } else {
            set_commitment(loan.commitment);
        }
        set_promissoryNote(loan.promissoryNote);
        set_bridgeFunded(loan.bridgeFunded ?? null);

        //Derive the lock state: checked whenever all three already agree, including
        //when all three are empty (a new loan starts locked so one entry fills all).
        const pn = loan.promissoryNote ?? null;
        set_sameAsCommitted(
            loan.loanTypeId === LoanType.Bridge && (loan.commitment ?? null) === pn && (loan.bridgeFunded ?? null) === pn
        );

        //Rental fields
        set_isRental(loan.isRental);
        set_amortizationTypeId(loan.amortizationTypeId);
        set_isAutoPay(loan.isAutoPay ?? false);
        set_ever30DaysDelinquent(loan.ever30DaysDelinquent ?? false);

        if (loan.statusId === LoanStatusType.Repaid && loan.repaidDate) {
            set_repaidDate(loan.repaidDate);
        }

        set_inForbearance(loan.inForbearance);
        set_forbearanceAgreementTerms(loan.forbearanceAgreementTerms);
        set_liquidity(loan.liquidity);
        set_entireStructure(loan.entireStructure ?? null);
    };

    const setCommitment = (val) => {
        if (!val || isNaN(val) || val <= 0) {
            set_commitment(null);
            return;
        }
        set_commitment(val);
    };

    const setPromissoryNote = (val) => {
        const next = !val || isNaN(val) || val <= 0 ? null : val;
        set_promissoryNote(next);
        //When locked, the other two amounts track Prom. Note/Com.
        if (sameAsCommitted) {
            set_commitment(next);
            set_bridgeFunded(next);
        }
    };

    //Unlike setCommitment, this preserves 0 as a valid value.
    const setBridgeFunded = (val) => {
        if (val === undefined || val === null || isNaN(val) || val < 0) {
            set_bridgeFunded(null);
            return;
        }
        set_bridgeFunded(val);
    };

    const toggleSameAsCommitted = (checked) => {
        set_sameAsCommitted(checked);
        if (checked) {
            //Sync immediately on check; on uncheck values are kept as-is.
            set_commitment(promissoryNote ?? null);
            set_bridgeFunded(promissoryNote ?? null);
        }
    };

    const toggleInForbearance = (val) => {
        if (inForbearance && val !== true && forbearanceAgreementTerms?.length > 0) {
            //prompt with alert (confirm / cancel)
            set_confirmFDILAAgreementTextRemovalWarningOpen(true);
            return;
        }
        set_inForbearance(val);
    };

    const onExpensesChange = (expenses) => {
        set_expenses([...expenses]);
    };

    const onChangesModalCancel = () => {
        set_changesModalOpen(false);
        set_redirectUrl(undefined);
        set_pendingCreateBudgetMode(undefined);
        set_pendingBudgetProperty(undefined);
    };

    const onChangesModalSubmit = async (model) => {
        set_changesModalOpen(false);
        const budgetMode = pendingCreateBudgetMode;
        const budgetProperty = pendingBudgetProperty;
        set_pendingCreateBudgetMode(undefined);
        set_pendingBudgetProperty(undefined);
        await saveLoan(model, true, undefined, budgetMode, budgetProperty);
    };

    const onFileChanges = (files) => {
        set_loanFiles(files);
    };

    const onGradeChange = (newGrade) => {
        if (loan.statusId !== LoanStatusType.Approved && newGrade !== grade) {
            set_gradeReasonVisible(true);
        }
        set_grade(newGrade);
    };

    const onGradeReasonSubmit = (reason) => {
        set_gradeReason(reason);
        set_gradeReasonVisible(false);
    };

    const onGradeReasonCancel = () => {
        set_grade(loan.grade ?? '');
        set_gradeReasonVisible(false);
    };

    if (!loan) {
        return null;
    }

    return (
        <div className='loan'>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '1100px', overflow: 'unset' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1>
                                    {currentLoanId > 0 ? 'Update ' : 'Add '}
                                    {loanTypeId === LoanType.SubNoteConstruction || loanTypeId === LoanType.SubNoteFixFlip
                                        ? 'Sub Note'
                                        : 'Loan'}
                                    &nbsp;-{' '}
                                    {statusId === LoanStatusType.Approved && noteDate
                                        ? getStatusTypeName(LoanStatusType.Outstanding)
                                        : getStatusTypeName(statusId)}
                                </h1>
                                <div style={styles.bfisearch}>
                                    <BfiNumberSearch
                                        loanTypeId={loanTypeId}
                                        onSelect={selectBfiNumber}
                                    />
                                </div>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={handleClose}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                        </div>
                        <div
                            className='main-content no-search'
                            style={{ padding: '0', width: '100%' }}
                        >
                            <form
                                name='addEditLoanForm'
                                id='addEditLoanForm'
                                onSubmit={(event) => validateSubmission(event, saveStages.validateStatusChangePrerequisites)}
                                className={'standard-form'}
                            >
                                <div
                                    className='scrollable-content'
                                    style={{ padding: '0px 25px 0 20px' }}
                                >
                                    <div
                                        style={{
                                            float: 'right',
                                            cursor: 'pointer',
                                        }}
                                        id='instructions'
                                        onMouseEnter={(e) => set_instructionsToolTipVisible(true)}
                                        onMouseLeave={(e) => set_instructionsToolTipVisible(false)}
                                    >
                                        <b>Required Fields</b>&nbsp;
                                        <FontAwesomeIcon icon={faQuestionCircle} />
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Setup
                                    </h2>
                                    <div
                                        className='row'
                                        style={styles.formRow}
                                    >
                                        <div
                                            className='columns small-12 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='loanTypeId'>
                                                Loan Type
                                                <span className='req'>*</span>
                                            </label>
                                            <select
                                                id='loanTypeId'
                                                value={loanTypeId}
                                                onChange={(event) => onLoanTypeChange(event.target.value)}
                                                required={true}
                                                disabled={(loan?.id ?? 0) > 0} /*!(masterNoteId && masterNoteId > 0)*/
                                            >
                                                {LoanTypeOptions.filter((a) => {
                                                    if (isSubNote) {
                                                        return (
                                                            a.id !== LoanType.MasterNote &&
                                                            (a.id === LoanType.SubNoteConstruction ||
                                                                a.id === LoanType.SubNoteFixFlip)
                                                        );
                                                    } else {
                                                        return (
                                                            a.id !== LoanType.MasterNote &&
                                                            a.id !== LoanType.SubNoteConstruction &&
                                                            a.id !== LoanType.SubNoteFixFlip
                                                        );
                                                    }
                                                }).map((fs) => {
                                                    return (
                                                        <option
                                                            key={fs.id}
                                                            value={fs.id}
                                                        >
                                                            {fs.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <LoanTermFields
                                            loanTypeId={loanTypeId}
                                            selectedTermKey={selectedTermKey}
                                            set_selectedTermKey={set_selectedTermKey}
                                            initialTermMonths={initialTermMonths}
                                            set_initialTermMonths={set_initialTermMonths}
                                            contractualExtension1Months={contractualExtension1Months}
                                            set_contractualExtension1Months={set_contractualExtension1Months}
                                            contractualExtension2Months={contractualExtension2Months}
                                            set_contractualExtension2Months={set_contractualExtension2Months}
                                            noteDate={noteDate}
                                            termDisabled={isSubNote || !loanTypeId}
                                            maturityDateDisabled={isSubNote}
                                        />
                                    </div>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='approvalDate'>
                                                BFI Approval {noteDate && <span className='req'>*</span>}
                                            </label>
                                            <DateBox
                                                id='approvalDate'
                                                value={approvalDate}
                                                onValueChange={set_approvalDate}
                                                required={noteDate}
                                            />
                                        </div>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='noteDate'>
                                                Note Date
                                                {(statusId === LoanStatusType.Outstanding ||
                                                    statusId === LoanStatusType.Repaid) && <span className='req'>*</span>}
                                            </label>
                                            <DateBox
                                                id='noteDate'
                                                value={noteDate}
                                                onValueChange={set_noteDate}
                                                required={
                                                    statusId === LoanStatusType.Outstanding || statusId === LoanStatusType.Repaid
                                                }
                                            />
                                        </div>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label
                                                htmlFor='maturityDate'
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                Maturity Date {noteDate && <span className='req'>*</span>}
                                            </label>
                                            <DateBox
                                                id='maturityDate'
                                                value={maturityDate}
                                                onValueChange={set_maturityDate}
                                                required={noteDate}
                                                disabled={isSubNote}
                                            />
                                            {maturityDateWarning && (
                                                <div style={styles.warning}>
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon
                                                                style={{ color: 'red' }}
                                                                icon={faExclamationTriangle}
                                                            />
                                                        </span>
                                                        <span className='text'>{maturityDateWarningText}</span>
                                                    </span>
                                                    &nbsp;Date Warning!
                                                </div>
                                            )}
                                        </div>
                                        {loan?.masterNoteBfiNumber && (
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='masterNoteBfiNumber'>Master Note BFI #</label>
                                                <input
                                                    id='masterNoteBfiNumber'
                                                    type='text'
                                                    value={loan?.masterNoteBfiNumber}
                                                    disabled={true}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className='row'>
                                        <div className='columns small-12 large-2'>
                                            <label htmlFor='bfiNumber'>BFI # {noteDate && <span className='req'>*</span>}</label>
                                            <BfiNumber
                                                id={'bfiNumber'}
                                                onChange={set_bfiNumber}
                                                value={bfiNumber}
                                                required={noteDate}
                                                isValid={isBfiNumberValid}
                                            />
                                        </div>
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='loanNumber'>
                                                Loan # {noteDate && <span className='req'>*</span>}
                                            </label>
                                            <LoanNumber
                                                id={'loanNumber'}
                                                onChange={set_loanNumber}
                                                value={loanNumber}
                                                required={noteDate}
                                                isValid={isLoanNumberValid}
                                            />
                                        </div>
                                        <div className='columns small-12 large-3'>
                                            <label htmlFor='pointOfContactId'>POC</label>
                                            <select
                                                id='pointOfContactId'
                                                value={pointOfContactId}
                                                onChange={(event) => set_pointOfContactId(event.target.value)}
                                            >
                                                <option
                                                    key=''
                                                    value=''
                                                ></option>
                                                {employees &&
                                                    employees.map((poc) => {
                                                        return (
                                                            <option
                                                                key={poc.key}
                                                                value={poc.key}
                                                            >
                                                                {poc.name}
                                                            </option>
                                                        );
                                                    })}
                                            </select>
                                        </div>
                                        <div className='columns small-12 large-3'>
                                            <label htmlFor='underwriterId'>Underwriter</label>
                                            <select
                                                id='underwriterId'
                                                value={underwriterId}
                                                onChange={(event) => set_underwriterId(event.target.value)}
                                            >
                                                <option
                                                    key=''
                                                    value=''
                                                ></option>
                                                {employees &&
                                                    employees.map((uw) => {
                                                        return (
                                                            <option
                                                                key={uw.key}
                                                                value={uw.key}
                                                            >
                                                                {uw.name}
                                                            </option>
                                                        );
                                                    })}
                                            </select>
                                        </div>
                                        <div className='columns small-12 large-5'>
                                            <label htmlFor='callCodeType'>Call Code</label>
                                            <select
                                                id='callCodeType'
                                                value={callCodeType ?? ''}
                                                onChange={(event) => {
                                                    const v = event.target.value;
                                                    set_callCodeType(v === '' ? null : parseInt(v));
                                                }}
                                            >
                                                <option value=''></option>
                                                {CallCodeTypeOptions.map((o) => (
                                                    <option
                                                        key={o.id}
                                                        value={o.id}
                                                    >
                                                        {o.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {(loan.statusId === LoanStatusType.Outstanding ||
                                            loan.statusId === LoanStatusType.Repaid) && (
                                            <>
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.inforbearance}
                                                >
                                                    <input
                                                        type='checkbox'
                                                        checked={inForbearance}
                                                        onChange={(e) => toggleInForbearance(e.target.checked)}
                                                        title='Does this loan have a FDILA agreement?'
                                                    />
                                                    &nbsp; Under a FDILA Agreement?
                                                </div>
                                                {inForbearance && (
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='forbearanceAgreementTerms'>
                                                            Terms of FDILA Agreement:
                                                        </label>
                                                        <textarea
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                            rows={3}
                                                            id='forbearanceAgreementTerms'
                                                            value={forbearanceAgreementTerms ?? ''}
                                                            onChange={(event) =>
                                                                set_forbearanceAgreementTerms(event.target.value)
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {loanTypeId === LoanType.BuildRent && (
                                        <>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Build Rent
                                            </h2>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='buildRentConversionDeadline'>
                                                        Convert Deadline
                                                        {noteDate && <span className='req'>*</span>}
                                                    </label>
                                                    <DateBox
                                                        id='buildRentConversionDeadline'
                                                        value={conversionDeadline}
                                                        onValueChange={set_conversionDeadline}
                                                        required={noteDate}
                                                        disabled={false}
                                                    />
                                                </div>

                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='conversionStageId'>Stage</label>
                                                    <select
                                                        id='conversionStageId'
                                                        value={loan?.conversionStageId ?? ''}
                                                        onChange={(event) =>
                                                            set_interestAcrualTypeId(parseInt(event.target.value))
                                                        }
                                                        disabled={true}
                                                    >
                                                        {BuildRentStageTypeOptions.map((fs) => {
                                                            return (
                                                                <option
                                                                    key={fs.id}
                                                                    value={fs.id}
                                                                >
                                                                    {fs.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>

                                                {loan?.conversionStageId === BuildRentStageType.Rent && (
                                                    <>
                                                        <div
                                                            className='columns small-12 large-2'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='conversionDate'>Converted Date</label>
                                                            <DateBox
                                                                id='conversionDate'
                                                                value={loan.conversionDate ? new Date(loan.conversionDate) : null}
                                                                disabled={true}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='conversionNotes'>Notes</label>
                                                            <textarea
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                                rows={3}
                                                                id='conversionNotes'
                                                                value={loan.conversionNotes ?? ''}
                                                                disabled={true}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {loan.statusId === LoanStatusType.Repaid && (
                                        <div className='row'>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='repaidDate'>
                                                    Repaid Date
                                                    <span className='req'>*</span>
                                                </label>
                                                <DateBox
                                                    id='repaidDate'
                                                    value={repaidDate}
                                                    onValueChange={set_repaidDate}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <ExtensionsGrid
                                        loanId={loan.id}
                                        maturityDate={maturityDate}
                                    />

                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Borrower
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 large-12'
                                            style={styles.formColumn}
                                        >
                                            {brokerAgreementId && (
                                                <div
                                                    id='brokerBorrowerLocked'
                                                    style={{
                                                        float: 'right',
                                                        color: 'darkred',
                                                        cursor: 'pointer',
                                                    }}
                                                    onMouseEnter={(e) => set_brokerBorrowerLocked(true)}
                                                    onMouseLeave={(e) => set_brokerBorrowerLocked(false)}
                                                >
                                                    {isSubNote && (
                                                        <>
                                                            Linked to Master Note <FontAwesomeIcon icon={faQuestionCircle} />
                                                        </>
                                                    )}
                                                    {!isSubNote && brokerAgreementLocked && (
                                                        <>
                                                            Linked to B-Client <FontAwesomeIcon icon={faQuestionCircle} />
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            <div style={{ width: '800px' }}>
                                                <BClientOrEntitySelector
                                                    borrowerEntityId={borrowerEntityId}
                                                    brokerAgreementId={brokerAgreementId}
                                                    onSelect={onBClientOrEntitySelection}
                                                    disabled={isSubNote || brokerAgreementLocked}
                                                />
                                                {bClientOrEntity === BClientOrEntitySelectorType.Entity && (
                                                    <BrokerSelect
                                                        brokerId={brokerId}
                                                        disabled={isSubNote}
                                                        onSelect={onBrokerSelect}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Loan
                                    </h2>
                                    <div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div
                                                className='columns small-12 large-3'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='promissoryNote'>
                                                    Prom. Note/Com.{noteDate && <span className='req'>*</span>}
                                                </label>
                                                <NumericFormat
                                                    thousandSeparator=','
                                                    prefix='$'
                                                    id='promissoryNote'
                                                    name='promissoryNote'
                                                    placeholder='$'
                                                    value={promissoryNote}
                                                    decimalScale={2}
                                                    fixedDecimalScale
                                                    allowNegative={false}
                                                    onValueChange={(values) => {
                                                        setPromissoryNote(values.floatValue);
                                                    }}
                                                    required={noteDate}
                                                    isAllowed={(values, sourceInfo) => {
                                                        const value = values?.floatValue;
                                                        return value === undefined || value === null || value > 0;
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className='columns small-12 large-3'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='commitment'>
                                                    Apprvd Loan Amt {noteDate && <span className='req'>*</span>}
                                                </label>
                                                <NumericFormat
                                                    thousandSeparator=','
                                                    prefix='$'
                                                    id='commitment'
                                                    name='commitment'
                                                    placeholder='$'
                                                    value={commitment}
                                                    decimalScale={2}
                                                    fixedDecimalScale
                                                    allowNegative={false}
                                                    onValueChange={(values) => {
                                                        setCommitment(values.floatValue);
                                                    }}
                                                    required={noteDate}
                                                    isAllowed={(values, sourceInfo) => {
                                                        const value = values?.floatValue;
                                                        return value === undefined || value === null || value > 0;
                                                    }}
                                                    disabled={(!isBridgeLoan && properties.length > 1) || sameAsCommitted}
                                                />
                                                <span className='helper'>
                                                    <span className='tooltip-trigger'>
                                                        <FontAwesomeIcon icon={faQuestionCircle} />
                                                    </span>
                                                    <span className='text'>
                                                        Approved Amount of the Loan.
                                                        <br />
                                                        <br />
                                                        This amount does not include the Origination Fee.
                                                    </span>
                                                </span>
                                            </div>
                                            {isBridgeLoan && (
                                                <>
                                                    <div
                                                        className='columns small-12 large-3'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='bridgeFunded'>
                                                            Bridge Funded {noteDate && <span className='req'>*</span>}
                                                        </label>
                                                        <NumericFormat
                                                            thousandSeparator=','
                                                            prefix='$'
                                                            id='bridgeFunded'
                                                            name='bridgeFunded'
                                                            placeholder='$'
                                                            value={bridgeFunded}
                                                            decimalScale={2}
                                                            fixedDecimalScale
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                setBridgeFunded(values.floatValue);
                                                            }}
                                                            required={noteDate}
                                                            isAllowed={(values) => {
                                                                const value = values?.floatValue;
                                                                return value === undefined || value === null || value >= 0;
                                                            }}
                                                            disabled={sameAsCommitted}
                                                        />
                                                    </div>
                                                    <div
                                                        className='columns small-12 large-3'
                                                        style={styles.inforbearance}
                                                    >
                                                        <input
                                                            type='checkbox'
                                                            id='sameAsCommitted'
                                                            checked={sameAsCommitted}
                                                            onChange={(e) => toggleSameAsCommitted(e.target.checked)}
                                                            title='Set Apprvd Loan Amt and Bridge Funded to Prom. Note/Com.'
                                                        />
                                                        &nbsp; Same as Committed
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='interestRate'>
                                                    {isSubNote
                                                        ? interestTypeId === 0
                                                            ? 'Interest Rate'
                                                            : 'Sprd over Prime'
                                                        : 'Interest Rate'}
                                                    {noteDate && <span className='req'>*</span>}
                                                </label>
                                                <NumericFormat
                                                    id='interestRate'
                                                    onValueChange={(values) => {
                                                        set_interestRate(values.floatValue);
                                                    }}
                                                    required={noteDate}
                                                    decimalScale={2}
                                                    allowNegative={false}
                                                    placeholder='%'
                                                    suffix={'%'}
                                                    value={interestRate}
                                                    displayType='input'
                                                    disabled={isSubNote}
                                                    isAllowed={(values, sourceInfo) => {
                                                        const value = values?.floatValue;
                                                        return value === undefined || value === null || value > 0;
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='interestAccrualTypeId'>Interest Accrual</label>
                                                <select
                                                    id='interestAcrualTypeId'
                                                    value={interestAcrualTypeId}
                                                    onChange={(event) => set_interestAcrualTypeId(parseInt(event.target.value))}
                                                >
                                                    {InterestAcrualTypeOptions.map((fs) => {
                                                        return (
                                                            <option
                                                                key={fs.id}
                                                                value={fs.id}
                                                            >
                                                                {fs.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            {!isSubNote && (
                                                <>
                                                    {loanTypeId !== LoanType.Rental &&
                                                        loanTypeId !== LoanType.BuildRent &&
                                                        loanTypeId !== LoanType.Bridge && (
                                                            <div
                                                                className='columns small-12 large-2'
                                                                style={styles.formColumn}
                                                            >
                                                                <label htmlFor='interestRateFloor'>Interest Rate Floor</label>
                                                                <NumericFormat
                                                                    id='interestRateFloor'
                                                                    onValueChange={(values) => {
                                                                        set_interestRateFloor(values.floatValue);
                                                                    }}
                                                                    required={false}
                                                                    decimalScale={2}
                                                                    allowNegative={false}
                                                                    placeholder='%'
                                                                    suffix={'%'}
                                                                    value={interestRateFloor}
                                                                    displayType='input'
                                                                    isAllowed={(values, sourceInfo) => {
                                                                        const value = values?.floatValue;
                                                                        return value === undefined || value === null || value > 0;
                                                                    }}
                                                                />
                                                            </div>
                                                        )}

                                                    {loanTypeId !== LoanType.Rental &&
                                                        loanTypeId !== LoanType.BuildRent &&
                                                        loanTypeId !== LoanType.Bridge && (
                                                            <div
                                                                className='columns small-12 large-2'
                                                                style={styles.formColumn}
                                                            >
                                                                <label htmlFor='interestRateCeiling'>Interest Rate Ceil.</label>
                                                                <NumericFormat
                                                                    id='interestRateCeiling'
                                                                    onValueChange={(values) => {
                                                                        set_interestRateCeiling(values.floatValue);
                                                                    }}
                                                                    required={false}
                                                                    decimalScale={2}
                                                                    allowNegative={false}
                                                                    placeholder='%'
                                                                    suffix={'%'}
                                                                    value={interestRateCeiling}
                                                                    displayType='input'
                                                                    isAllowed={(values, sourceInfo) => {
                                                                        const value = values?.floatValue;
                                                                        return value === undefined || value === null || value > 0;
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                </>
                                            )}
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='reserveRate'>Reserve Rate</label>
                                                <NumericFormat
                                                    id='reserveRate'
                                                    onValueChange={(values) => {
                                                        set_reserveRate(values.floatValue);
                                                    }}
                                                    required={false}
                                                    decimalScale={2}
                                                    allowNegative={false}
                                                    suffix={'%'}
                                                    placeholder='%'
                                                    value={reserveRate}
                                                    displayType='input'
                                                    disabled={isSubNote}
                                                    isAllowed={(values, sourceInfo) => {
                                                        const value = values?.floatValue;
                                                        return value === undefined || value === null || value > 0;
                                                    }}
                                                />
                                            </div>

                                            <div
                                                className='columns small-12 large-1'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='grade'>Grade</label>
                                                <select
                                                    id={'grade'}
                                                    value={grade ?? ''}
                                                    onChange={(event) => onGradeChange(parseInt(event.target.value))}
                                                >
                                                    <option value=''></option>
                                                    {LoanGradeTypeOptions.filter((x) => x.isLoc === isSubNote).map((o) => {
                                                        return (
                                                            <option
                                                                key={o.id}
                                                                value={o.id}
                                                            >
                                                                {o.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                <GradeReasonModal
                                                    isOpen={gradeReasonVisible}
                                                    reason={gradeReason}
                                                    onSubmit={onGradeReasonSubmit}
                                                    onCancel={onGradeReasonCancel}
                                                />
                                            </div>
                                            {isSubNote && (
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='entireStructure'>Entire Structure</label>
                                                    <select
                                                        id='entireStructure'
                                                        value={
                                                            entireStructure === null || entireStructure === undefined
                                                                ? ''
                                                                : entireStructure
                                                                  ? '1'
                                                                  : '0'
                                                        }
                                                        onChange={(event) => {
                                                            switch (event.target.value) {
                                                                case '':
                                                                    set_entireStructure(null);
                                                                    break;
                                                                case '0':
                                                                    set_entireStructure(false);
                                                                    break;
                                                                case '1':
                                                                    set_entireStructure(true);
                                                                    break;
                                                            }
                                                        }}
                                                    >
                                                        <option value=''></option>
                                                        <option value='0'>No</option>
                                                        <option value='1'>Yes</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        {!isSubNote && (
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='underwritingFee'>Underwriting Fee</label>
                                                    <NumericFormat
                                                        thousandSeparator=','
                                                        prefix='$'
                                                        id='underwritingFee'
                                                        name='underwritingFee'
                                                        placeholder='$'
                                                        value={underwritingFee}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        onValueChange={(values) => {
                                                            set_underwritingFee(values.floatValue);
                                                        }}
                                                        required={false}
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='originationFee'>Origination Fee</label>
                                                    <NumericFormat
                                                        prefix='$'
                                                        thousandSeparator=','
                                                        id='originationFee'
                                                        placeholder='$'
                                                        value={originationFee}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        onValueChange={(values) => {
                                                            set_originationFee(values.floatValue);
                                                        }}
                                                        required={false}
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                        disabled={properties.length > 1}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='brokerShare'>Broker Share</label>
                                                    <NumericFormat
                                                        prefix='$'
                                                        id='brokerShare'
                                                        thousandSeparator=','
                                                        placeholder='$'
                                                        value={brokerShare}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        onValueChange={(values) => {
                                                            set_brokerShare(values.floatValue);
                                                        }}
                                                        required={false}
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='brokerSharePercent'>Broker Share %</label>
                                                    <input
                                                        type='text'
                                                        id='brokerSharePercent'
                                                        value={
                                                            brokerShare &&
                                                            brokerShare > 0 &&
                                                            commitment &&
                                                            commitment > 0 &&
                                                            ((brokerShare / commitment) * 100).toFixed(2) + '%'
                                                        }
                                                        disabled={true}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='brokerAdditional'>Broker Addl.</label>
                                                    <NumericFormat
                                                        prefix='$'
                                                        thousandSeparator=','
                                                        id='brokerAdditional'
                                                        placeholder='$'
                                                        value={brokerAdditional}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        onValueChange={(values) => {
                                                            set_brokerAdditional(values.floatValue);
                                                        }}
                                                        required={false}
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='brokerAddlPercent'>Broker Addl. %</label>
                                                    <input
                                                        type='text'
                                                        id='brokerAddlPercent'
                                                        value={
                                                            brokerAdditional &&
                                                            brokerAdditional > 0 &&
                                                            commitment &&
                                                            commitment > 0 &&
                                                            ((brokerAdditional / commitment) * 100).toFixed(2) + '%'
                                                        }
                                                        disabled={true}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className='row'
                                        style={styles.formRow}
                                    >
                                        <div
                                            className='columns small-12 large-2'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='liquidity'>Liquidity</label>
                                            <NumericFormat
                                                prefix='$'
                                                thousandSeparator=','
                                                id='liquidity'
                                                placeholder='$'
                                                value={liquidity}
                                                decimalScale={2}
                                                allowNegative={false}
                                                onValueChange={(values) => {
                                                    set_liquidity(values.floatValue);
                                                }}
                                                required={false}
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value > 0;
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {isRental && (
                                        <>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Loan Rental Data
                                            </h2>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='amortizationTypeId'>
                                                        Amortization {noteDate && <span className='req'>*</span>}
                                                    </label>
                                                    <select
                                                        id={'amortizationTypeId'}
                                                        value={amortizationTypeId ?? ''}
                                                        onChange={(event) => set_amortizationTypeId(parseInt(event.target.value))}
                                                        required={noteDate}
                                                    >
                                                        <option value=''></option>
                                                        {AmortizationTypeOptions.map((o) => {
                                                            return (
                                                                <option
                                                                    key={o.id}
                                                                    value={o.id}
                                                                >
                                                                    {o.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='isAutoPay'>AutoPay?</label>
                                                    <select
                                                        id={'isAutoPay'}
                                                        value={isAutoPay ? '1' : '0'}
                                                        onChange={(event) => set_isAutoPay(parseInt(event.target.value) === 1)}
                                                        required={false}
                                                    >
                                                        <option value='0'>No</option>
                                                        <option value='1'>Yes</option>
                                                    </select>
                                                </div>
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='ever30DaysDelinquent'>Ever 30+ Days Delinquent?</label>
                                                    <select
                                                        id={'ever30DaysDelinquent'}
                                                        value={ever30DaysDelinquent ? '1' : '0'}
                                                        onChange={(event) =>
                                                            set_ever30DaysDelinquent(parseInt(event.target.value) === 1)
                                                        }
                                                        required={false}
                                                    >
                                                        <option value='0'>No</option>
                                                        <option value='1'>Yes</option>
                                                    </select>
                                                </div>
                                                <div
                                                    data-tip
                                                    data-for='amort'
                                                    className='columns small-12 large-3'
                                                >
                                                    {loan.statusId === LoanStatusType.Outstanding && (
                                                        <AmortizationTable
                                                            loanId={loanId}
                                                            interestRate={interestRate}
                                                            noteDate={noteDate}
                                                            amortizationTypeId={amortizationTypeId}
                                                            commitment={commitment}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {loan.id > 0 && (
                                        <LoanFilesGrid
                                            title='Loan Files'
                                            fileTypeOptions={LoanFileTypeOptions}
                                            files={loanFiles}
                                            onChange={(files) => onFileChanges(files)}
                                        />
                                    )}
                                    {loan.id === 0 && (
                                        <div
                                            style={{
                                                marginTop: '10px',
                                                color: 'silver',
                                            }}
                                        >
                                            <h2 className='decorated'>Loan Files</h2>
                                            Available after first save.
                                        </div>
                                    )}

                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Expenses
                                    </h2>
                                    <div
                                        className='accordion'
                                        data-accordion=''
                                        data-allow-all-closed='true'
                                        data-multi-expand='true'
                                    >
                                        <LoanExpensesGrid
                                            id='preClosingExpenses'
                                            expenseArray={expenses}
                                            onChange={onExpensesChange}
                                            expenseSectionType={LoanExpenseCategory.PreClosing}
                                            loanStatus={statusId}
                                        />
                                        <LoanExpensesGrid
                                            id='postClosingExpenses'
                                            expenseArray={expenses}
                                            onChange={onExpensesChange}
                                            expenseSectionType={LoanExpenseCategory.PostClosing}
                                            loanStatus={statusId}
                                        />
                                    </div>
                                    <LoanProperties
                                        bfiNumber={bfiNumber}
                                        commitment={commitment}
                                        originationFee={originationFee}
                                        properties={properties}
                                        onChange={onPropertiesChange}
                                        onRemove={onPropertiesRemove}
                                        loanTypeId={loan.isRental ? LoanType.Rental : loanTypeId}
                                        statusId={loan.statusId}
                                        required={!!noteDate}
                                        validateSubmission={validateSubmission}
                                    />
                                    {properties.length > 1 && loanTypeId !== LoanType.MasterNote && (
                                        <>
                                            <LoanPortfolioOverageRepaymentGrid loanId={loan.id} />
                                        </>
                                    )}
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='submit'
                                        className='button primary'
                                        title='Save all changes'
                                    >
                                        Save
                                    </button>
                                    <button
                                        type='button'
                                        className='button hollow'
                                        data-dismiss='modal'
                                        onClick={handleClose}
                                        title='Abandon changes and close this window'
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Dialog>
            <ConfirmModal
                isOpen={confirmNumbersModalOpen}
                text={confirmNumbersModalText}
                confirmCallback={confirmNumber_confirm}
                cancelCallback={confirmNumber_cancel}
            />
            <ConfirmModal
                isOpen={confirmMoveToOutstandingModalOpen}
                confirmCallback={confirmMoveToOutstanding_confirm}
                cancelCallback={confirmMoveToOutstanding_cancel}
            >
                {confirmMoveToOutstandingModalText.map((t) => {
                    return <DialogContentText id='alert-dialog-description'>{t}</DialogContentText>;
                })}
            </ConfirmModal>
            <ConfirmModal
                isOpen={confirmMaturityDateModalOpen}
                text={confirmMaturityDateModalText}
                confirmCallback={confirmMaturityDate_confirm}
                cancelCallback={confirmMaturityDate_cancel}
                confirmButtonText='Continue'
                cancelButtonText='Cancel'
            />
            <ConfirmModal
                isOpen={confirmFDILAAgreementTextRemovalWarningOpen}
                text={confirmFDILAAgreementTextRemovalWarningText}
                confirmCallback={confirmFDILAAgreementTextRemoval_confirm}
                cancelCallback={confirmFDILAAgreementTextRemoval_cancel}
                confirmButtonText='Continue'
                cancelButtonText='Cancel'
            />
            <InformModal
                isOpen={informModalOpen}
                text={informModalMessage}
                confirmCallback={onInformModalConfirm}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <Tooltip
                target={'#instructions'}
                visible={instructionsToolTipVisible}
                hideOnOutsideClick={false}
            >
                <div style={{ width: '500px', textAlign: 'left' }}>
                    <div
                        style={{
                            width: '100%',
                            margin: '5px 0',
                            whiteSpace: 'break-spaces',
                        }}
                    >
                        In the <b>Approved</b> stage, all fields are optional except 'Borrower' and 'Loan Type'. After 'Note Date'
                        is completed (which automatically moves the Loan to <b>Outstanding</b>) then the following fields are
                        required:
                    </div>
                    <div style={{ width: '30%', float: 'left' }}>
                        <b>Setup</b>
                        <ul>
                            <li>BFI Approval</li>
                            <li>Note Date</li>
                            <li>Terms</li>
                            <li>Maturity Date</li>
                            <li>BFI #</li>
                            <li>Loan #</li>
                            <li>Loan Type</li>
                        </ul>
                    </div>
                    <div style={{ width: '30%', float: 'left' }}>
                        <b>Property</b>
                        <ul>
                            <li>Legal Description</li>
                            <li>Structure Type</li>
                            <li>Units</li>
                            <li>Street Address 1</li>
                            <li>City</li>
                            <li>State</li>
                            <li>Zip</li>
                            <li>Appraised Value</li>
                        </ul>
                    </div>
                    <div style={{ width: '30%', float: 'left' }}>
                        <b>Loan</b>
                        <ul>
                            <li>Apprvd Loan Amt</li>
                            <li>Interest Rate</li>
                            <li>Interest Accrual</li>
                        </ul>
                        <b>Rental</b>
                        <ul>
                            <li>Amortization</li>
                            <li>Is AutoPay</li>
                            <li>Purpose</li>
                        </ul>
                    </div>
                </div>
            </Tooltip>
            <Tooltip
                target='#brokerBorrowerLocked'
                visible={brokerBorrowerLocked}
                hideOnOutsideClick={false}
            >
                {isSubNote && (
                    <div style={{ textAlign: 'left' }}>
                        This Loan is linked to Master Note. <br />
                        Broker and Borrower may NOT be changed.
                    </div>
                )}
                {!isSubNote && (
                    <div style={{ textAlign: 'left' }}>
                        This Loan is linked to a B-Client. <br />
                        Broker and Borrower may NOT be changed.
                    </div>
                )}
            </Tooltip>
            <LoanChangeReasonModal
                isOpen={changesModalOpen}
                onCancel={onChangesModalCancel}
                onSubmit={onChangesModalSubmit}
            />
        </div>
    );
};

export default EditStdLoanModal;
